import { keyframes, mergeStyleSets, useTheme } from '@fluentui/react';
import { IPFontIcon, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { useEffect, useMemo, useRef, useState } from 'react';

export type CopilotActivityItemState = 'active' | 'done' | 'error';

export interface CopilotActivityItem {
  // Stable identity used to detect when a new item arrives (drives the scroll-up animation).
  key: string;
  label: string;
  state: CopilotActivityItemState;
}

interface ICopilotActivityTickerProps {
  // Full ordered list; the ticker keeps only the last maxVisibleCount rows visible and scrolls the rest up.
  items: CopilotActivityItem[];
  maxVisibleCount?: number;
  rowHeight?: number;
  rowGap?: number;
  scrollAnimationMs?: number;
}

const defaultMaxVisibleCount = 4;
const defaultRowHeight = 20;
const defaultRowGap = 2;
const defaultScrollAnimationMs = 220;

// Presentational activity ticker: a clipped window that shows a small spinner before the active
// (last) item, a check before completed items and an error glyph before failed ones, animating the
// oldest row up and out as new items arrive. Callers own their surrounding container chrome and map
// their domain events/steps to the generic item model.
export const CopilotActivityTicker = (props: ICopilotActivityTickerProps): JSX.Element | null => {
  const theme = useTheme();
  const maxVisibleCount = props.maxVisibleCount ?? defaultMaxVisibleCount;
  const rowHeight = props.rowHeight ?? defaultRowHeight;
  const rowGap = props.rowGap ?? defaultRowGap;
  const scrollAnimationMs = props.scrollAnimationMs ?? defaultScrollAnimationMs;

  const targetItems = useMemo(() => props.items.slice(-maxVisibleCount), [props.items, maxVisibleCount]);
  const latestItem = props.items[props.items.length - 1];
  const latestKey = latestItem ? latestItem.key : '';
  const previousLatestKeyRef = useRef<string>('');
  const [renderedItems, setRenderedItems] = useState<CopilotActivityItem[]>([]);
  const [isScrollAnimating, setIsScrollAnimating] = useState<boolean>(false);

  const listScrollUp = keyframes({
    from: {
      transform: 'translateY(0)',
    },
    to: {
      transform: `translateY(-${rowHeight + rowGap}px)`,
    },
  });

  const styles = mergeStyleSets({
    viewport: {
      overflow: 'hidden',
    },
    track: {
      display: 'flex',
      flexDirection: 'column',
      gap: rowGap,
    },
    trackScroll: {
      animationDuration: `${scrollAnimationMs}ms`,
      animationName: listScrollUp,
      animationTimingFunction: 'ease-out',
    },
    activity: {
      alignItems: 'center',
      display: 'flex',
      gap: 8,
      maxWidth: '100%',
      minHeight: rowHeight,
      transformOrigin: 'bottom left',
      width: '100%',
    },
    fadedActivity: {
      opacity: 0.48,
    },
    icon: {
      alignItems: 'center',
      display: 'flex',
      flexShrink: 0,
      height: 16,
      justifyContent: 'center',
      width: 16,
    },
    checkIcon: {
      color: theme.semanticColors.successIcon,
      fontSize: 12,
    },
    errorIcon: {
      color: theme.semanticColors.errorIcon,
      fontSize: 12,
    },
    spinner: {
      lineHeight: 0,
    },
    text: {
      minHeight: 16,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  });

  useEffect(() => {
    if (!latestKey) {
      previousLatestKeyRef.current = '';
      setRenderedItems([]);
      setIsScrollAnimating(false);

      return undefined;
    }

    if (previousLatestKeyRef.current === '') {
      previousLatestKeyRef.current = latestKey;
      setRenderedItems(targetItems);

      return undefined;
    }

    if (previousLatestKeyRef.current === latestKey) {
      // the same rows: take their current label and state (a finished call, a counting step) in place
      const itemsByKey = new Map(targetItems.map((item) => [item.key, item]));
      setRenderedItems((currentItems) =>
        currentItems.length === 0 ? targetItems : currentItems.map((item) => itemsByKey.get(item.key) ?? item),
      );

      return undefined;
    }

    // A new latest item arrived: render one extra (oldest) row and translate the track up by one row,
    // then commit the settled window once the animation completes.
    previousLatestKeyRef.current = latestKey;
    setRenderedItems((currentItems) => {
      const latest = targetItems[targetItems.length - 1];
      if (!latest) {
        return targetItems;
      }

      const previousWindow = currentItems.length > 0 ? currentItems.slice(-maxVisibleCount) : [];

      if (previousWindow.length < maxVisibleCount) {
        return targetItems;
      }

      return [...previousWindow, latest].slice(-(maxVisibleCount + 1));
    });
    setIsScrollAnimating(targetItems.length === maxVisibleCount);

    const timeoutId = window.setTimeout(() => {
      setRenderedItems(targetItems);
      setIsScrollAnimating(false);
    }, scrollAnimationMs);

    return () => window.clearTimeout(timeoutId);
  }, [latestKey, targetItems, maxVisibleCount, scrollAnimationMs]);

  if (renderedItems.length === 0) {
    return null;
  }

  const trackClassName = [styles.track, isScrollAnimating ? styles.trackScroll : ''].filter(Boolean).join(' ');
  const visibleCount = Math.min(targetItems.length, maxVisibleCount);
  const viewportHeight = visibleCount * rowHeight + Math.max(visibleCount - 1, 0) * rowGap;

  const renderIcon = (state: CopilotActivityItemState): JSX.Element => {
    if (state === 'active') {
      return <IPSpinner size={IPSpinnerSize.small} styles={{ root: styles.spinner }} />;
    }

    if (state === 'error') {
      return <IPFontIcon iconName="ErrorBadge" className={styles.errorIcon} />;
    }

    return <IPFontIcon iconName="CheckMark" className={styles.checkIcon} />;
  };

  return (
    <div className={styles.viewport} style={{ height: viewportHeight }}>
      <div className={trackClassName}>
        {renderedItems.map((item, index) => {
          const isFaded = index === 0 && renderedItems.length > 1;
          const className = [styles.activity, isFaded ? styles.fadedActivity : ''].filter(Boolean).join(' ');

          return (
            <IPStack horizontal className={className} key={item.key}>
              <span className={styles.icon} aria-hidden="true">
                {renderIcon(item.state)}
              </span>
              <IPText variant="small" className={styles.text} styles={{ root: { color: 'inherit' } }}>
                {item.label}
              </IPText>
            </IPStack>
          );
        })}
      </div>
    </div>
  );
};
